import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Gapless playback of streamed 24 kHz / 16-bit / mono PCM frames (Int16) via Web Audio.
 * Frames are scheduled back-to-back on the AudioContext clock so playback has no gaps.
 * Shared by SAM push-to-talk replies and interactive voice. `stop()` cancels all scheduled
 * audio (barge-in / Stop button).
 */
export interface PCMAudioStream {
  enqueue: (pcm: ArrayBuffer | Int16Array | Uint8Array) => void;
  stop: () => void;
  /** Mute/unmute playback (speaker) without tearing down the audio context. */
  setMuted: (muted: boolean) => void;
  /** Route playback to a specific output device (`''` = system default). Chromium-desktop only —
   *  a no-op where `AudioContext.setSinkId` is unsupported (e.g. iOS Safari). */
  setSinkId: (deviceId: string) => Promise<void>;
  /** True while scheduled audio is actually sounding (reactive — drives the "SAM speaking" indicator).
   *  Stays true across the small gaps between streamed TTS chunks; drops as soon as the last
   *  scheduled source finishes or `stop()` is called. */
  playing: boolean;
}

const SAMPLE_RATE = 24000;

// AudioContext.setSinkId is not in the TS DOM lib yet.
type CtxWithSink = AudioContext & { setSinkId?: (id: string) => Promise<void> };

export function usePCMAudioStream(): PCMAudioStream {
  const ctxRef = useRef<AudioContext | null>(null);
  const nextStartRef = useRef(0);
  const sourcesRef = useRef<AudioBufferSourceNode[]>([]);
  // Gain node between the sources and the speakers so playback can be muted (gain 0) without
  // dropping the context. `mutedRef` persists the choice across context recreation.
  const gainRef = useRef<GainNode | null>(null);
  const mutedRef = useRef(false);
  // Chosen output device id (''=default). Persisted across context recreation so a newly created
  // ctx routes to the same speaker the user picked.
  const sinkIdRef = useRef('');
  // Reactive "audio is sounding" signal (sourcesRef itself isn't reactive). The grace timer bridges
  // the small silent gaps between back-to-back streamed TTS chunks so `playing` doesn't flicker
  // false between them — only drops once nothing new shows up for a beat, or on stop().
  const [playing, setPlaying] = useState(false);
  const endGraceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const PLAYBACK_END_GRACE_MS = 200;

  const ensureCtx = useCallback((): AudioContext => {
    if (!ctxRef.current || ctxRef.current.state === 'closed') {
      ctxRef.current = new AudioContext({ sampleRate: SAMPLE_RATE });
      nextStartRef.current = ctxRef.current.currentTime;
      const gain = ctxRef.current.createGain();
      gain.gain.value = mutedRef.current ? 0 : 1;
      gain.connect(ctxRef.current.destination);
      gainRef.current = gain;
      const ctx = ctxRef.current as CtxWithSink;
      if (sinkIdRef.current && typeof ctx.setSinkId === 'function') {
        ctx.setSinkId(sinkIdRef.current).catch(() => undefined);
      }
    }
    return ctxRef.current;
  }, []);

  const enqueue = useCallback((pcm: ArrayBuffer | Int16Array | Uint8Array) => {
    let int16: Int16Array;
    if (pcm instanceof Int16Array) int16 = pcm;
    else if (pcm instanceof Uint8Array) int16 = new Int16Array(pcm.buffer, pcm.byteOffset, Math.floor(pcm.byteLength / 2));
    else int16 = new Int16Array(pcm);
    if (!int16.length) return;

    const ctx = ensureCtx();
    if (ctx.state === 'suspended') ctx.resume().catch(() => undefined);

    const buffer = ctx.createBuffer(1, int16.length, SAMPLE_RATE);
    const channel = buffer.getChannelData(0);
    for (let i = 0; i < int16.length; i++) channel[i] = int16[i] / 0x8000;

    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(gainRef.current ?? ctx.destination);

    const startAt = Math.max(ctx.currentTime, nextStartRef.current);
    src.start(startAt);
    nextStartRef.current = startAt + buffer.duration;

    sourcesRef.current.push(src);
    if (endGraceRef.current) { clearTimeout(endGraceRef.current); endGraceRef.current = null; }
    setPlaying(true);
    src.onended = () => {
      sourcesRef.current = sourcesRef.current.filter((s) => s !== src);
      if (sourcesRef.current.length === 0 && !endGraceRef.current) {
        endGraceRef.current = setTimeout(() => {
          endGraceRef.current = null;
          if (sourcesRef.current.length === 0) setPlaying(false);
        }, PLAYBACK_END_GRACE_MS);
      }
    };
  }, [ensureCtx]);

  const stop = useCallback(() => {
    sourcesRef.current.forEach((s) => { try { s.stop(); } catch { /* already stopped */ } });
    sourcesRef.current = [];
    if (ctxRef.current) nextStartRef.current = ctxRef.current.currentTime;
    if (endGraceRef.current) { clearTimeout(endGraceRef.current); endGraceRef.current = null; }
    setPlaying(false);
  }, []);

  const setMuted = useCallback((muted: boolean) => {
    mutedRef.current = muted;
    if (gainRef.current) gainRef.current.gain.value = muted ? 0 : 1;
  }, []);

  const setSinkId = useCallback(async (deviceId: string) => {
    sinkIdRef.current = deviceId || '';
    const ctx = ctxRef.current as CtxWithSink | null;
    if (ctx && typeof ctx.setSinkId === 'function') {
      try { await ctx.setSinkId(deviceId || ''); } catch { /* unsupported / invalid id */ }
    }
  }, []);

  // iOS/Android suspend the AudioContext when the screen locks or the tab goes to background.
  // Resume it immediately when the page becomes visible again so the next audio chunk plays
  // without waiting for a user interaction to unblock the context.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && ctxRef.current?.state === 'suspended') {
        ctxRef.current.resume().catch(() => undefined);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  useEffect(() => () => { if (endGraceRef.current) clearTimeout(endGraceRef.current); }, []);

  return useMemo(() => ({ enqueue, stop, setMuted, setSinkId, playing }), [enqueue, stop, setMuted, setSinkId, playing]);
}
